import { useCallback, useMemo, useEffect } from "react";
import { useAppSelector } from "@store/index";
import { crosstabConfigSelector } from "@store/slices/crosstab-config-slice";
import {
  useGetDatasetQuestionsQuery,
  useGetDatasetsQuery,
  useGetWeightConfigurationsQuery,
} from "@/store/api/dataset-api";

interface PerSegmentWeight {
  surveyId: string;
  configurationId: string;
  datasetElementQuestionId: string;
  datasetElementVariableId: string;
  weightVariableLabel: string;
  crossColumnVariableId?: string;
}

interface UsePerSegmentWeightsProps {
  datasetId: string;
  columnLabels: string[];
  columnVariableIds?: Record<string, string>; // Map of column label -> variable ID
  onWeightsChange: (weights: Record<string, PerSegmentWeight>) => void;
}

export const usePerSegmentWeights = ({
  datasetId,
  columnLabels,
  columnVariableIds = {},
  onWeightsChange,
}: UsePerSegmentWeightsProps) => {
  // Read perSegmentWeights directly from Redux instead of maintaining local state
  const { currentConfig } = useAppSelector(crosstabConfigSelector);
  const perSegmentWeights = useMemo(
    () => currentConfig.perSegmentWeights || {},
    [currentConfig.perSegmentWeights],
  );

  // Fetch all datasets to get the surveyId
  const { data: datasets = [] } = useGetDatasetsQuery({});

  // Find the current dataset and get the first surveyId
  const surveyId = useMemo(() => {
    const dataset = datasets.find((d) => d.id === datasetId);
    return dataset?.surveyIds?.[0] || "";
  }, [datasets, datasetId]);

  const { data: weightConfigData } = useGetWeightConfigurationsQuery(
    datasetId,
    { skip: !datasetId },
  );

  const isMultiSurvey = useMemo(() => {
    const dataset = datasets.find((d) => d.id === datasetId);
    const surveyCount = dataset?.surveyIds?.length ?? 0;
    if (surveyCount <= 1) return false;

    const configs = weightConfigData?.weightConfigurations ?? [];
    if (configs.length !== surveyCount) return true;

    const first = configs[0];
    const allSame = configs.every(
      (c) =>
        c.datasetElementQuestionId === first.datasetElementQuestionId &&
        c.variableLabel === first.variableLabel,
    );
    return !allSame;
  }, [datasets, datasetId, weightConfigData]);

  // Fetch weight questions for the dataset using surveyId
  // Filter by FLOAT question type and multidimensional = true
  // IMPORTANT: This query is isolated and should not interfere with row/column question selection
  const {
    data: weightQuestionsData,
    isLoading: isLoadingWeights,
    error: weightConfigError,
  } = useGetDatasetQuestionsQuery(
    {
      id: datasetId,
      survey_id: surveyId,
      question_type: ["FLOAT"],
      multidimensional: true,
      page: 1,
      page_size: 100, // Get all weight questions
    },
    {
      skip: !datasetId || !surveyId,
      // Keep this data in cache longer to avoid refetching
      // This prevents interference with row/column question queries
      refetchOnMountOrArgChange: false,
      refetchOnFocus: false,
      refetchOnReconnect: false,
    },
  );

  // Transform questions into WeightConfiguration format
  const weightConfigurations = useMemo(() => {
    if (!weightQuestionsData?.questions || !surveyId) {
      return [];
    }

    return weightQuestionsData.questions.map((question) => ({
      configurationId: question.datasetElementId,
      surveyId: surveyId,
      surveyName: "", // Not available from questions endpoint, but not displayed in UI
      datasetElementQuestionId: question.datasetElementId,
      questionLabel: question.label || question.title || question.name,
      hidden: question.hidden,
    }));
  }, [weightQuestionsData, surveyId]);

  // Update column weight - calls the Redux action directly
  const updateColumnWeight = useCallback(
    (
      columnLabel: string,
      surveyId: string,
      configurationId: string,
      datasetElementQuestionId: string,
      datasetElementVariableId: string,
      weightVariableLabel: string,
    ) => {
      const crossColumnVariableId = columnVariableIds[columnLabel];

      console.log(`💾 updateColumnWeight for column "${columnLabel}":`, {
        weightVariableLabel,
        crossColumnVariableId,
        columnVariableIds,
      });

      onWeightsChange({
        ...perSegmentWeights,
        [columnLabel]: {
          surveyId,
          configurationId,
          datasetElementQuestionId,
          datasetElementVariableId,
          weightVariableLabel,
          crossColumnVariableId, // Add column variable ID
        },
      });
    },
    [perSegmentWeights, onWeightsChange, columnVariableIds],
  );

  // Clear weight for a specific column (use default weight)
  const clearColumnWeight = useCallback(
    (columnLabel: string) => {
      console.log(`🧹 Clearing weight for column: ${columnLabel}`);
      const updated = { ...perSegmentWeights };
      delete updated[columnLabel];
      onWeightsChange(updated);
    },
    [perSegmentWeights, onWeightsChange],
  );

  // Clear all weights (set all to default weight)
  const clearAllWeights = useCallback(() => {
    console.log("🧹 Clearing all weights - resetting to default");
    onWeightsChange({});
  }, [onWeightsChange]);

  // Get weight for a specific column
  const getColumnWeight = useCallback(
    (columnLabel: string): PerSegmentWeight | undefined => {
      return perSegmentWeights[columnLabel];
    },
    [perSegmentWeights],
  );

  // Check if all columns are configured
  const isAllColumnsConfigured = useMemo(() => {
    if (columnLabels.length === 0) return false;
    return columnLabels.every((label) => {
      const weight = perSegmentWeights[label];
      return (
        weight && weight.configurationId && weight.datasetElementVariableId
      );
    });
  }, [columnLabels, perSegmentWeights]);

  // Clean up weights for columns that no longer exist
  // IMPORTANT: Only clean up if we have columns loaded (don't clean when columnLabels is empty during initial load)
  useEffect(() => {
    // Skip cleanup if no columns are loaded yet (e.g., when editing widget before preview data loads)
    if (columnLabels.length === 0) {
      return;
    }

    const currentColumns = new Set(columnLabels);
    const existingColumns = Object.keys(perSegmentWeights);

    // Check if any columns need to be removed
    const needsCleanup = existingColumns.some(
      (col) => !currentColumns.has(col),
    );

    if (needsCleanup) {
      console.log("🧹 Cleaning up weights for removed columns");
      const filtered: Record<string, PerSegmentWeight> = {};
      for (const [columnLabel, weight] of Object.entries(perSegmentWeights)) {
        if (currentColumns.has(columnLabel)) {
          filtered[columnLabel] = weight;
        }
      }
      onWeightsChange(filtered);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnLabels]); // Intentionally NOT including perSegmentWeights or onWeightsChange to avoid infinite loops

  return {
    datasetId,
    surveyId,
    isMultiSurvey,
    perSegmentWeights,
    weightConfigurations,
    isLoadingWeights,
    weightConfigError,
    updateColumnWeight,
    clearColumnWeight,
    clearAllWeights,
    getColumnWeight,
    isAllColumnsConfigured,
  };
};
